import React, { useCallback, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { clampNumber, formatNumericValue, parseNumericInput } from '../utils/numberInput';

export default function NumericField({
  value,
  onValueChange,
  label,
  mode = 'decimal', // 'integer' | 'decimal'
  allowNegative = false,
  min,
  max,
  decimals,
  fullWidth = true,
  InputProps,
  inputProps,
  prefix,
  suffix,
  disabled,
  size,
  helperText,
  required,
  placeholder,
}) {
  const [raw, setRaw] = useState(() => {
    if (value == null || !Number.isFinite(value)) return '';
    return String(value);
  });

  const inputRef = React.useRef(null);

  const displayValue = useMemo(() => {
    const isFocused = typeof document !== 'undefined' && document.activeElement && inputRef.current && document.activeElement === inputRef.current;
    if (isFocused) return raw;
    if (raw !== '') return raw;
    if (value == null || !Number.isFinite(value)) return '';
    return String(value);
  }, [raw, value]);
  const adornmentStart = prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : (InputProps?.startAdornment || null);
  const adornmentEnd = suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : (InputProps?.endAdornment || null);

  const handleChange = useCallback((e) => {
    const nextRaw = e.target.value;
    setRaw(nextRaw);
    const { number } = parseNumericInput(nextRaw, { mode, allowNegative });
    if (onValueChange) onValueChange(number);
  }, [mode, allowNegative, onValueChange]);

  const handleBlur = useCallback(() => {
    const { number } = parseNumericInput(raw, { mode, allowNegative });
    const clamped = clampNumber(number, { min, max });
    if (onValueChange) onValueChange(clamped ?? null);
    if (clamped == null) {
      setRaw('');
    } else {
      const formatted = formatNumericValue(clamped, { mode, decimals });
      setRaw(formatted);
    }
  }, [raw, mode, allowNegative, min, max, decimals, onValueChange]);

  return (
    <TextField
      fullWidth={fullWidth}
      label={label}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      type="text"
      inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
      InputProps={{ ...InputProps, startAdornment: adornmentStart, endAdornment: adornmentEnd }}
      inputProps={inputProps}
      disabled={disabled}
      size={size}
      helperText={helperText}
      required={required}
      placeholder={placeholder}
      inputRef={inputRef}
    />
  );
}


